import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const SellerModeContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SellerModeProvider({ children }) {
  const [activeSeller, setActiveSeller] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);

  // Restore from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('seller_mode');
    if (stored) {
      try { setActiveSeller(JSON.parse(stored)); } catch {}
    }
  }, []);

  // Persist to sessionStorage
  useEffect(() => {
    if (activeSeller) {
      sessionStorage.setItem('seller_mode', JSON.stringify(activeSeller));
    } else {
      sessionStorage.removeItem('seller_mode');
    }
  }, [activeSeller]);

  // Axios interceptor: attach X-Seller-Mode header when active
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(config => {
      if (activeSeller?.user_id) {
        config.headers['X-Seller-Mode'] = activeSeller.user_id;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, [activeSeller]);

  const fetchSellers = useCallback(async (search = '') => {
    setLoadingSellers(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (search) params.set('search', search);
      const res = await axios.get(`${API_URL}/api/admin/sellers?${params}`, { withCredentials: true });
      const data = res.data;
      // Handle both paginated and array responses
      const list = Array.isArray(data) ? data : (data.sellers || []);
      setSellers(list);
      return list;
    } catch {
      return [];
    } finally { setLoadingSellers(false); }
  }, []);

  const enterSellerMode = (seller) => {
    setActiveSeller({
      user_id: seller.user_id,
      name: seller.name,
      email: seller.email,
      business_name: seller.business_name || '',
    });
  };

  const exitSellerMode = () => {
    setActiveSeller(null);
  };

  const value = {
    activeSeller,
    isSellerMode: !!activeSeller,
    sellers,
    loadingSellers,
    fetchSellers,
    enterSellerMode,
    exitSellerMode,
  };

  return <SellerModeContext.Provider value={value}>{children}</SellerModeContext.Provider>;
}

export function useSellerMode() {
  const context = useContext(SellerModeContext);
  if (!context) throw new Error('useSellerMode must be used within SellerModeProvider');
  return context;
}
